import React from 'react';
import { Copy, Lightbulb, Plus, RotateCw, Save, Trash2 } from 'lucide-react';

import {
  Card, EmptyState, IconButton, PageHeader, PhonePage, PillButton, PrimaryButton, StatusPill,
} from '@/components/phone/primitives';
import { INPUT_CLASS, inputStyle, SelectField } from '@/components/phone/FieldControls';
import { Colors } from '@/components/phone/theme';
import { fmt } from './EstimatorPanel';

/**
 * The estimator, one column wide.
 *
 * On a laptop every input runs along the top so the sheet and the imposition
 * are on screen together. A phone has no "together": you type the box, scroll
 * to the answer, and come back. So the answer sits directly under the inputs
 * as a grid of tiles, the imposition and the cost sheet follow as cards, and
 * the rate master — which prices everything above it — is last, where it is
 * out of the way until a rate is wrong. Every value is the page's own state;
 * this only draws it, so the phone prices exactly what the laptop prices.
 */

const TILE_TONE = {
  blue: Colors.accent.DEFAULT,
  accent: Colors.accent.DEFAULT,
  warn: Colors.semantic.caution,
};

function Label({ children }) {
  return <p className="mb-1.5 text-[11px] font-medium" style={{ color: Colors.text.secondary }}>{children}</p>;
}

function NumberInput({ label, suffix, value, onChange, placeholder, testID }) {
  return (
    <label className="block min-w-0">
      <Label>
        {label}
        {!!suffix && <span style={{ color: Colors.text.tertiary }}> ({suffix})</span>}
      </Label>
      <input
        type="number"
        inputMode="decimal"
        data-testid={testID}
        className={`${INPUT_CLASS} tabular-nums`}
        style={inputStyle}
        value={value ?? ''}
        placeholder={placeholder == null ? '0' : String(placeholder)}
        onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
      />
    </label>
  );
}

function TextInput({ label, ...props }) {
  return (
    <label className="block min-w-0">
      <Label>{label}</Label>
      <input className={INPUT_CLASS} style={inputStyle} {...props} />
    </label>
  );
}

function Tile({ label, value, tone, testID }) {
  return (
    <div
      data-testid={testID}
      className="min-w-0 rounded-[14px] border p-3.5"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <p className="truncate text-[11px] font-semibold uppercase tracking-[0.6px]" style={{ color: Colors.text.secondary }}>{label}</p>
      <p className="mt-1 truncate text-[18px] font-bold tabular-nums -tracking-[0.3px]" style={{ color: TILE_TONE[tone] || Colors.text.primary }}>
        {value}
      </p>
    </div>
  );
}

function Orientation({ label, layout: o, chosen }) {
  return (
    <div
      className="rounded-[10px] border p-3"
      style={{
        borderColor: chosen ? Colors.accent.DEFAULT : Colors.border.default,
        backgroundColor: chosen ? Colors.accent.wash : Colors.background.primary,
      }}
    >
      <p className="flex items-center justify-between text-[10px] uppercase tracking-wider" style={{ color: Colors.text.tertiary }}>
        {label}
        {chosen && <span style={{ color: Colors.accent.DEFAULT }}>in use</span>}
      </p>
      <p className="text-[18px] font-bold leading-tight tabular-nums" style={{ color: Colors.text.primary }}>
        {o.ups_across_width} × {o.ups_along_length} = {o.total_ups}
      </p>
      <p className="text-[10px]" style={{ color: Colors.text.tertiary }}>
        {fmt(o.unit_width_mm, 1)} × {fmt(o.unit_length_mm, 1)} mm per up
      </p>
    </div>
  );
}

function Imposition({ result, forced, onRotate }) {
  const imp = result.imposition;
  const chosen = imp.recommended_orientation === 'rotated' ? imp.rotated : imp.upright;
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <Orientation label="Upright" layout={imp.upright} chosen={imp.upright === chosen} />
        <Orientation label="Rotated 90°" layout={imp.rotated} chosen={imp.rotated === chosen} />
      </div>
      <div
        className="mt-3 space-y-2 rounded-[10px] border p-3"
        style={{ backgroundColor: Colors.semantic.cautionWash, borderColor: '#E8DCC4' }}
      >
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider" style={{ color: Colors.semantic.caution }}>
          <Lightbulb className="h-3.5 w-3.5" /> Layout advisory
        </p>
        <p className="text-[13px] leading-5" style={{ color: Colors.text.primary }}>{chosen.advice_width}</p>
        <p className="text-[13px] leading-5" style={{ color: Colors.text.primary }}>{chosen.advice_length}</p>
        <p className="text-[13px] leading-5" style={{ color: Colors.text.primary }}>{imp.rotation_advice}</p>
        <button
          type="button"
          onClick={onRotate}
          data-testid="rotate-btn"
          className="flex h-11 w-full items-center justify-center gap-1.5 rounded-[10px] border text-[13px] font-semibold"
          style={{ backgroundColor: Colors.background.primary, borderColor: '#E8DCC4', color: Colors.semantic.caution }}
        >
          <RotateCw className="h-4 w-4" />
          {forced === 'rotated' ? 'Back to upright' : 'Rotate 90°'}
        </button>
      </div>
    </>
  );
}

/** The priced rows, one under another: what, how many at what rate, and the amount. */
function CostSheet({ sheet, rateCount }) {
  if (!sheet) {
    return <p className="py-4 text-center text-[12px]" style={{ color: Colors.text.tertiary }}>Enter the box and the sheet.</p>;
  }
  if (!sheet.rows.length) {
    return (
      <p className="py-4 text-center text-[12px]" style={{ color: Colors.text.tertiary }}>
        {rateCount === 0
          ? 'No rates in the master yet. Add them under Rate master and the cost sheet prices itself.'
          : 'Pick a cost group to price this job.'}
      </p>
    );
  }
  const totals = [
    ['Subtotal', sheet.subtotal],
    [`Wastage ${sheet.wastage_pct}%`, sheet.wastage_amount],
    [`Margin ${sheet.margin_pct}%`, sheet.margin_amount],
  ];
  return (
    <div data-testid="phone-cost-sheet">
      {sheet.rows.map((row, idx) => (
        <div key={`${row.id || row.process}-${idx}`} className="flex items-start justify-between gap-3 border-b py-2.5 first:pt-0"
          style={{ borderColor: Colors.border.subtle }}>
          <span className="min-w-0">
            <span className="block text-[13px] font-medium" style={{ color: Colors.text.primary }}>
              {row.process}
              {!!row.remark && <span className="ml-1.5 text-[11px]" style={{ color: Colors.text.tertiary }}>{row.remark}</span>}
            </span>
            <span className="block text-[11px] tabular-nums" style={{ color: Colors.text.secondary }}>
              {fmt(row.units, 2)} {row.unit} × {fmt(row.rate, 2)} per {fmt(row.per)}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-1.5 text-[13px] font-semibold tabular-nums" style={{ color: Colors.text.primary }}>
            {fmt(row.amount, 2)}
            {row.minimum_applied && <StatusPill label="min" tone="caution" />}
          </span>
        </div>
      ))}
      {totals.map(([label, value]) => (
        <div key={label} className="flex items-center justify-between py-1.5 text-[12px] tabular-nums first:pt-3"
          style={{ color: Colors.text.secondary }}>
          <span>{label}</span><span>{fmt(value, 2)}</span>
        </div>
      ))}
      <div className="mt-1 flex items-center justify-between border-t-2 py-2.5 text-[15px] font-bold tabular-nums"
        style={{ borderColor: Colors.accent.DEFAULT, color: Colors.accent.DEFAULT }}>
        <span>Total</span><span>₹{fmt(sheet.total, 2)}</span>
      </div>
      <div className="flex items-center justify-between text-[13px] font-semibold tabular-nums" style={{ color: Colors.text.primary }}>
        <span>Cost per unit</span><span>₹{fmt(sheet.cost_per_unit, 4)}</span>
      </div>
    </div>
  );
}

export default function PhoneCosting({
  styles, style, inputs, set, setNested, result, summary,
  trimFields, spacingFields, costGroups, units,
  rates, newRate, setNewRate, onAddRate, onRemoveRate,
  estimates, onStartSimilar, onSave,
}) {
  const styleOptions = styles.map((s) => ({ value: s.code, label: `${s.code} · ${s.name}` }));

  return (
    <PhonePage testID="box-costing-phone">
      <PageHeader
        title="Box Costing"
        subtitle="Geometry, imposition, then price"
        action={(
          <PillButton onClick={onSave} testID="save-estimate-btn">
            <Save className="h-4 w-4" /> Save
          </PillButton>
        )}
      />

      <div className="flex flex-col gap-3 px-5 pb-8">
        <Card title="Box" aside={style?.name} testID="phone-box-card">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Style</Label>
              <SelectField name="style_code" label="Box style" value={inputs.style_code} options={styleOptions}
                onChange={(v) => set({ style_code: v })} placeholder="Pick a box style" />
            </div>
            <NumberInput label="Length" suffix="mm" value={inputs.length_mm} onChange={(v) => set({ length_mm: v })} testID="phone-length" />
            <NumberInput label="Width" suffix="mm" value={inputs.width_mm} onChange={(v) => set({ width_mm: v })} />
            <NumberInput label="Height" suffix="mm" value={inputs.height_mm} onChange={(v) => set({ height_mm: v })} />
            <NumberInput label="Quantity" suffix="pcs" value={inputs.quantity} onChange={(v) => set({ quantity: v })} />
          </div>
          {!!style?.description && (
            <p className="mt-3 text-[12px] leading-5" style={{ color: Colors.text.secondary }}>{style.description}</p>
          )}
        </Card>

        <Card title="Sheet, trims and spacing">
          <div className="grid grid-cols-2 gap-3">
            <NumberInput label="Sheet width" suffix="mm" value={inputs.sheet_width_mm} onChange={(v) => set({ sheet_width_mm: v })} />
            <NumberInput label="Sheet length" suffix="mm" value={inputs.sheet_length_mm} onChange={(v) => set({ sheet_length_mm: v })} />
            <NumberInput label="GSM" value={inputs.gsm} onChange={(v) => set({ gsm: v })} />
            {trimFields.map(([key, label]) => (
              <NumberInput key={key} label={label} suffix="mm" value={inputs.trims[key] ?? ''}
                onChange={(v) => setNested('trims', key, v)} />
            ))}
            {spacingFields.map(([group, key, label]) => (
              <NumberInput key={key} label={label} suffix="mm" value={inputs[group][key] ?? ''}
                onChange={(v) => setNested(group, key, v)} />
            ))}
          </div>
        </Card>

        {style?.allowances?.length > 0 && (
          <Card title="Allowances" aside="blank if derived from the size">
            <div className="grid grid-cols-2 gap-3">
              {style.allowances.map((name) => (
                <NumberInput
                  key={name}
                  label={name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  suffix="mm"
                  value={inputs.allowances[name] ?? ''}
                  placeholder={result?.geometry?.allowances?.[name] ?? 'auto'}
                  onChange={(v) => setNested('allowances', name, v)}
                />
              ))}
            </div>
          </Card>
        )}

        <Card title="Pricing">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Cost group</Label>
              <SelectField name="cost_group" label="Cost group" value={inputs.cost_group || ''} options={costGroups}
                onChange={(v) => set({ cost_group: v })} placeholder="Pick a group" />
            </div>
            <NumberInput label="Wastage" suffix="%" value={inputs.wastage_pct || ''} onChange={(v) => set({ wastage_pct: v || 0 })} />
            <NumberInput label="Margin" suffix="%" value={inputs.margin_pct || ''} onChange={(v) => set({ margin_pct: v || 0 })} />
          </div>
        </Card>

        {result ? (
          <div className="grid grid-cols-2 gap-3" data-testid="costing-summary">
            {summary.filter(Boolean).map((s) => <Tile key={s.key} label={s.label} value={s.value} tone={s.tone} testID={`tile-${s.key}`} />)}
          </div>
        ) : (
          <p className="rounded-[14px] border px-4 py-3 text-[12px]"
            style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default, color: Colors.text.tertiary }}>
            Enter the box and the sheet — the summary fills itself in.
          </p>
        )}

        <Card title="Imposition"
          aside={result ? `${result.imposition.usable_width_mm} × ${result.imposition.usable_length_mm} mm usable` : undefined}>
          {result ? (
            <Imposition
              result={result}
              forced={inputs.force_orientation}
              onRotate={() => set({ force_orientation: inputs.force_orientation === 'rotated' ? 'upright' : 'rotated' })}
            />
          ) : (
            <p className="py-4 text-center text-[12px]" style={{ color: Colors.text.tertiary }}>Enter the box and the sheet.</p>
          )}
        </Card>

        <Card title="Cost sheet" aside="quantities come off the imposition">
          <CostSheet sheet={result?.cost_sheet} rateCount={rates.length} />
        </Card>

        <Card title="Rate master" aside={`${rates.length} rates`} testID="phone-rate-master">
          <p className="mb-3 text-[12px] leading-5" style={{ color: Colors.text.secondary }}>
            A rate is <span className="font-medium">cost group → process</span>, charged per block of units, with a
            minimum for short runs.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <TextInput label="Cost group" placeholder="Board" value={newRate.cost_group} data-testid="rate-cost-group"
              onChange={(e) => setNewRate((p) => ({ ...p, cost_group: e.target.value }))} />
            <TextInput label="Process" placeholder="Duplex 300" value={newRate.process} data-testid="rate-process"
              onChange={(e) => setNewRate((p) => ({ ...p, process: e.target.value }))} />
            <NumberInput label="Rate" value={newRate.rate} testID="rate-rate"
              onChange={(v) => setNewRate((p) => ({ ...p, rate: v === '' ? '' : String(v) }))} />
            <NumberInput label="Per" value={newRate.per} placeholder="1"
              onChange={(v) => setNewRate((p) => ({ ...p, per: v || 1 }))} />
            <div>
              <Label>Unit</Label>
              <SelectField name="rate_unit" label="Unit" value={newRate.unit} options={units}
                onChange={(v) => setNewRate((p) => ({ ...p, unit: v }))} />
            </div>
            <TextInput label="Remark" placeholder="Optional" value={newRate.remark}
              onChange={(e) => setNewRate((p) => ({ ...p, remark: e.target.value }))} />
          </div>
          <div className="mt-3">
            <PrimaryButton onClick={onAddRate} testID="add-rate-btn"><Plus className="h-4 w-4" /> Add rate</PrimaryButton>
          </div>

          <ul className="mt-4" data-testid="phone-rates-list">
            {rates.length === 0 ? (
              <li className="py-4 text-center text-[12px]" style={{ color: Colors.text.tertiary }}>
                No rates yet. Until there are, the cost sheet has nothing to price.
              </li>
            ) : rates.map((r) => (
              <li key={r.id} data-testid={`rate-card-${r.id}`} className="flex items-center gap-3 border-t py-2.5"
                style={{ borderColor: Colors.border.subtle }}>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium" style={{ color: Colors.text.primary }}>
                    <span style={{ color: Colors.text.secondary }}>{r.cost_group} → </span>{r.process}
                  </span>
                  <span className="block truncate text-[11px] tabular-nums" style={{ color: Colors.text.secondary }}>
                    ₹{r.rate} per {r.per} {r.unit}
                    {r.minimum ? ` · min ₹${r.minimum}` : ''}
                    {r.remark ? ` · ${r.remark}` : ''}
                  </span>
                </span>
                <IconButton icon={Trash2} label={`Remove ${r.process}`} onClick={() => onRemoveRate(r.id)} testID={`remove-rate-${r.id}`} />
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Saved estimates" aside={`${estimates.length}`} testID="phone-estimates">
          {estimates.length === 0 ? (
            <EmptyState icon={Copy} message="No saved estimates yet" />
          ) : (
            <ul>
              {estimates.map((e) => (
                <li key={e.id} className="border-t py-3 first:border-t-0 first:pt-0" style={{ borderColor: Colors.border.subtle }}>
                  <div className="flex items-start justify-between gap-3">
                    <span className="min-w-0">
                      <span className="block truncate text-[14px] font-bold" style={{ color: Colors.text.primary }}>{e.name}</span>
                      <span className="block truncate text-[12px]" style={{ color: Colors.text.secondary }}>
                        {[e.customer_name, e.product].filter(Boolean).join(' · ') || '—'}
                      </span>
                    </span>
                    <StatusPill label={e.style_code} tone="accent" />
                  </div>
                  <p className="mt-1.5 text-[12px] tabular-nums" style={{ color: Colors.text.secondary }}>
                    {e.quantity?.toLocaleString()} pcs · {e.total_ups} ups · {e.margin_pct}% margin
                  </p>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <span className="text-[14px] font-semibold tabular-nums" style={{ color: Colors.text.primary }}>
                      ₹{e.total?.toLocaleString()}
                      <span className="ml-1.5 text-[11px] font-normal" style={{ color: Colors.text.tertiary }}>₹{e.cost_per_unit} / unit</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => onStartSimilar(e.id)}
                      data-testid={`similar-${e.id}`}
                      className="flex h-10 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-semibold"
                      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default, color: Colors.accent.DEFAULT }}
                    >
                      <Copy className="h-3.5 w-3.5" /> Similar to
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </PhonePage>
  );
}
